import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const RetentionChart = ({ data, selectedMetric }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date?.getMonth() + 1}/${date?.getDate()}`;
  };

  const getChartData = () => {
    if (!data || data?.length === 0) return [];

    // Group by date and aggregate
    const groupedData = {};
    data?.forEach(item => {
      const date = item?.date;
      if (!groupedData?.[date]) {
        groupedData[date] = {
          date,
          engagement: 0,
          workouts: 0,
          classes: 0,
          posts: 0,
          count: 0
        };
      }
      groupedData[date].engagement += item?.engagement_score || 0;
      groupedData[date].workouts += item?.workout_count || 0;
      groupedData[date].classes += item?.class_attendance_count || 0;
      groupedData[date].posts += item?.community_posts_count || 0;
      groupedData[date].count += 1;
    });

    // Calculate averages
    return Object?.values(groupedData)?.map(item => ({
      date: item?.date,
      engagement: Math?.round(item?.engagement / item?.count),
      workouts: Math?.round(item?.workouts / item?.count),
      classes: Math?.round(item?.classes / item?.count),
      posts: Math?.round(item?.posts / item?.count)
    }));
  };

  const chartData = getChartData();

  const getMetricConfig = () => {
    switch (selectedMetric) {
      case 'engagement':
        return {
          title: 'Average Engagement Score Trend',
          dataKey: 'engagement',
          color: 'var(--color-primary)',
          label: 'Engagement Score'
        };
      case 'retention':
        return {
          title: 'Member Activity Trends',
          dataKey: 'workouts',
          color: 'var(--color-success)',
          label: 'Workouts'
        };
      case 'churn':
        return {
          title: 'Class Attendance Trend',
          dataKey: 'classes',
          color: 'var(--color-warning)',
          label: 'Classes'
        };
      case 'lifecycle':
        return {
          title: 'Community Engagement Trend',
          dataKey: 'posts',
          color: 'var(--color-accent)',
          label: 'Posts'
        };
      default:
        return {
          title: 'Engagement Trend',
          dataKey: 'engagement',
          color: 'var(--color-primary)',
          label: 'Score'
        };
    }
  };

  const config = getMetricConfig();

  if (!chartData || chartData?.length === 0) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">{config?.title}</h2>
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="TrendingUp" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No engagement data available for the selected period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">{config?.title}</h2>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} />
          <span>Average per member per day</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDate}
            stroke="var(--color-muted-foreground)"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="var(--color-muted-foreground)"
            style={{ fontSize: '12px' }}
          />
          <Tooltip
            labelFormatter={(label) => `Date: ${label}`}
            contentStyle={{
              backgroundColor: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              color: 'var(--color-foreground)'
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey={config?.dataKey}
            name={config?.label}
            stroke={config?.color}
            strokeWidth={2}
            dot={{ fill: config?.color, r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RetentionChart;